import React from 'react';
import { DetailsHeader } from './DetailsHeader';
import { SharedNotesTimeline } from './SharedNotesTimeline';
import { ValueLabel } from '@shared/components/ValueLabel';
import { CheckCircle, Calendar, Users, MapPin, Mail, Info } from 'lucide-react';
import { useRequestDetail } from '../../../shared/hook';
import { getLoginDataFromStorage } from '@shared/utils/loginUtils';
import { useTranslation } from 'react-i18next';

export const ReconnectionDetails = ({ data }: { data: any }) => {
  const { t } = useTranslation();
  const { remoteUtilityId } = getLoginDataFromStorage();
  const { data: requestDetail, isLoading } = useRequestDetail({
    remote_utility_id: remoteUtilityId,
    id: data?.id,
  });

  const details = requestDetail?.result || data;

  if (isLoading) return <div className="p-6 text-center text-slate-500">{t("Loading...")}</div>;

  return (
    <div className="flex flex-col h-full bg-slate-50/50 pb-6">
      <DetailsHeader 
        icon={<CheckCircle size={18} />}
        iconBgColor="bg-green-100"
        iconTextColor="text-green-600"
        title={details?.utilitySupportRequest?.name || t("Reconnection")}
        status={details?.statusDisplay}
        id={details?.requestNo ?? t("N/A")}
        submittedDate={details?.createdDate ? new Date(details.createdDate).toLocaleDateString() : t("N/A")}
      />
      <div className="p-6 space-y-4">
        {/* RECONNECTION & SCHEDULE */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-4"><CheckCircle size={16}/> {t("Reconnection")}</h4>
            <div className="space-y-4">
              <ValueLabel label={t("Reason")} value={details?.utilitySupportRequest?.supportRequestType || t("Renovation Complete")} />
              <ValueLabel label={t("Reconnect Date")} value={details?.requestDate ? new Date(details.requestDate).toLocaleDateString() : t("N/A")} />
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-4"><Calendar size={16}/> {t("Schedule")}</h4>
            <div className="space-y-4">
              <ValueLabel label={t("Submitted")} value={details?.createdDate ? new Date(details.createdDate).toLocaleDateString() : t("N/A")} />
              <ValueLabel label={t("Time Slot")} value="—" />
            </div>
          </div>
        </div>

        {/* ACCOUNT STRUCTURE */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-4"><Users size={16}/> {t("Account Structure Preference")}</h4>
          <div className="grid grid-cols-2 gap-y-4 gap-x-4">
            <ValueLabel label={t("Account Type")} value={t("Sub-Account")} />
            <ValueLabel label={t("Structure Choice")} value={t("Keep under the same master account")} />
          </div>
        </div>

        {/* SERVICE ADDRESS */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-2"><MapPin size={16}/> {t("Service Address")}</h4>
            <p className="text-sm font-medium text-foreground truncate cursor-default">1333 Oak Street, Denver, CO 80202, USA</p>
        </div>

        {/* MAILING ADDRESS */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-4"><Mail size={16}/> {t("Mailing Address")}</h4>
            <p className="text-sm font-medium text-foreground truncate cursor-default">1333 Oak Street, Denver, CO 80202, USA</p>
        </div>

        {/* ADDITIONAL NOTES */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-3"><Info size={16}/> {t("Additional Notes")}</h4>
          <p className="text-sm text-gray-700 leading-relaxed">{details?.utilitySupportRequest?.longDescription || t("No additional notes provided.")}</p>
        </div>

        {/* TABS */}
        <SharedNotesTimeline data={data} />
      </div>
    </div>
  );
};
